import React from 'react';
import classnames from 'classnames';

// Types
import { AiSuggestionWorkspaceItem } from 'Shared/types/AiSuggestionWorkspaceItem.type';

// Utils
import { handleLaunchExternalLink } from 'Shared/utils/external-link';

// Hooks
import { useToggle } from 'Shared/utils/hooks';

// Components
import { WpContainer } from 'Wpds/components/container';
import { WpBadge } from 'Wpds/components/badge';
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpTextTruncate from 'Wpds/components/text-truncate/WpTextTruncate';
import { WpTooltip, WpTooltipContent, WpTooltipTrigger } from 'Wpds/components/tooltip';

// Styles
import './AgendaAiItem.scss';

type Props = {
  aiSuggestion: AiSuggestionWorkspaceItem;
  handleAcceptAiSuggestion: Function;
  handleRejectAiSuggestion: Function;
};

const AgendaAiItem = ({ aiSuggestion, handleAcceptAiSuggestion, handleRejectAiSuggestion }: Props) => {
  const [isAccepting, toggleIsAccepting] = useToggle(false);
  const [isRejecting, togglieIsRejecting] = useToggle(false);

  const handleAcceptClick = () => {
    if (isAccepting) return;
    toggleIsAccepting();
    handleAcceptAiSuggestion({ aiSuggestionSlug: aiSuggestion.slug }).then(() => toggleIsAccepting());
  };

  const handleRejectClick = () => {
    if (isRejecting) return;
    togglieIsRejecting();
    handleRejectAiSuggestion({ aiSuggestionSlug: aiSuggestion.slug }).then(() => togglieIsRejecting());
  };

  const handleClickBadge = () => {
    const { slackLink } = aiSuggestion?.message;
    if (slackLink) handleLaunchExternalLink({ href: slackLink });
  };

  const baseClassName = 'AgendaAiItem';
  const classes = classnames(baseClassName, isAccepting || isRejecting ? `${baseClassName}--removing` : '');
  const aiSuggestionHasAdditionalInformation = !!aiSuggestion.additionalInformation;

  return (
    <WpContainer className={classes}>
      <div className={`${baseClassName}__body`}>
        <WpTextTruncate
          text={`${aiSuggestion.body}${
            aiSuggestionHasAdditionalInformation ? ` - ${aiSuggestion.additionalInformation}` : ''
          }`}
          numberOfLines={2}
          showTooltip
        />
        {aiSuggestion.message.channelName && (
          <div onClick={handleClickBadge} role="button" className={`${baseClassName}__body__channel`}>
            <WpBadge color="blue" size="sm">
              <WpIcon name="hashtag" size="sm" />
              {aiSuggestion.message.channelName}
            </WpBadge>
          </div>
        )}
      </div>
      <div className={`${baseClassName}__actions`}>
        <WpTooltip>
          <WpTooltipTrigger>
            <div
              className={`${baseClassName}__actions__btn ${baseClassName}__actions__btn--accept`}
              role="button"
              onClick={handleAcceptClick}
            >
              <WpIcon name="plus-circle" size="md" />
            </div>
          </WpTooltipTrigger>
          <WpTooltipContent>Add to agenda</WpTooltipContent>
        </WpTooltip>
        <WpTooltip>
          <WpTooltipTrigger>
            <div
              className={`${baseClassName}__actions__btn ${baseClassName}__actions__btn--reject`}
              role="button"
              onClick={handleRejectClick}
            >
              <WpIcon name="times" size="md" />
            </div>
          </WpTooltipTrigger>
          <WpTooltipContent>Dismiss</WpTooltipContent>
        </WpTooltip>
      </div>
    </WpContainer>
  );
};

export default AgendaAiItem;
